import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, TrendingUp } from "lucide-react";
import { Card } from "@samklang/ui";
import type { AnnanRole, Perspective } from "@samklang/schemas";
import { useKartlaggningStore } from "@/stores/kartlaggning-store";
import { formatDate, metricPoints, perspectiveShortLabel } from "./lib";

interface Props {
  readonly kartlaggningId: string;
}

export function KartlaggningDevelopmentScreen({ kartlaggningId }: Props) {
  const navigate = useNavigate();
  const kartlaggning = useKartlaggningStore((store) => store.getKartlaggning(kartlaggningId));
  const all = useKartlaggningStore((store) => store.listKartlaggningar)();

  const data = useMemo(() => {
    if (!kartlaggning) return undefined;
    const participant = all.filter((item) => item.participantReference && item.participantReference === kartlaggning.participantReference);
    const sorted = [...participant].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    const points = metricPoints(kartlaggning.participantReference, all);
    const byMetric = new Map<string, { label: string; rows: { perspective: Perspective; role?: AnnanRole; cells: (number | null)[] }[] }>();
    for (const metric of [...new Set(points.map((point) => point.metricKey))]) {
      const series = points.filter((point) => point.metricKey === metric);
      const label = series[0]?.metricLabel ?? metric;
      const perspectives = [...new Set(series.map((point) => point.perspective))];
      const rows = perspectives.map((perspective) => {
        const role = series.find((point) => point.perspective === perspective)?.role;
        const cells = sorted.map((kartlaggningItem) => {
          const match = series.find((point) => point.kartlaggningId === kartlaggningItem.id && point.perspective === perspective);
          return match ? match.value : null;
        });
        return {
          perspective,
          ...(role !== undefined ? { role } : {}),
          cells,
        };
      });
      byMetric.set(metric, { label, rows });
    }
    return { sorted, metrics: [...byMetric.values()] };
  }, [kartlaggning, all]);

  if (!kartlaggning || !data) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <Card><h1 className="font-display text-2xl font-semibold">Kartläggningen finns inte.</h1></Card>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-4xl px-5 py-8 sm:px-8">
      <button onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId", params: { kartlaggningId } })} className="flex items-center gap-1 text-sm font-semibold text-primary">
        <ArrowLeft size={16} /> Till kartläggningen
      </button>
      <h1 className="mt-3 flex items-center gap-2 font-display text-3xl font-semibold"><TrendingUp className="text-primary" /> Utveckling över tid</h1>
      <p className="mt-2 text-foreground-muted">
        Skattningar per perspektiv och kartläggning för elev med lokalt id <strong>{kartlaggning.participantReference || "—"}</strong> ({data.sorted.length} kartläggning(ar)).
      </p>

      {data.sorted.length === 0 ? (
        <Card className="mt-6"><p className="text-foreground-muted">Inga kartläggningar för denna deltagare.</p></Card>
      ) : data.metrics.length === 0 ? (
        <Card className="mt-6"><p className="text-foreground-muted">Inga skattningsfrågor besvarade ännu.</p></Card>
      ) : (
        <div className="mt-6 grid gap-4">
          {data.metrics.map((metric) => (
            <Card key={metric.label} className="overflow-x-auto px-5 py-4">
              <h2 className="font-display text-lg font-semibold">{metric.label}</h2>
              <table className="mt-3 w-full border-collapse text-left text-sm">
                <thead>
                  <tr>
                    <th className="py-2 pr-4 font-semibold text-foreground-muted">Perspektiv</th>
                    {data.sorted.map((item) => (
                      <th key={item.id} className="py-2 pr-4 font-semibold text-foreground-muted">{formatDate(item.createdAt)}</th>
                    ))}
                    <th className="py-2 font-semibold text-foreground-muted">Förändring</th>
                  </tr>
                </thead>
                <tbody>
                  {metric.rows.map((row) => {
                    const values = row.cells.filter((cell): cell is number => cell !== null);
                    const change = values.length >= 2 ? `${values[0]} → ${values[values.length - 1]}` : values.length === 1 ? `${values[0]}` : "—";
                    return (
                      <tr key={row.perspective} className="border-t border-border/50">
                        <td className="py-2 pr-4 font-semibold">{perspectiveShortLabel(row.perspective, row.role)}</td>
                        {data.sorted.map((item) => {
                          const index = data.sorted.findIndex((candidate) => candidate.id === item.id);
                          const value = row.cells[index];
                          return <td key={item.id} className="py-2 pr-4">{value ?? "—"}</td>;
                        })}
                        <td className="py-2 font-semibold text-primary">{change}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          ))}
        </div>
      )}
      <p className="mt-4 text-xs text-foreground-subtle">Skattningarna är deltagarnas egna svar, inte diagnoser eller automatiska bedömningar.</p>
    </section>
  );
}
